import { useEffect, useMemo, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { usePoll } from "@/hooks/usePoll";
import { errMsg, get } from "@/lib/api";
import { fmt, fmtPct, fmtQty, tone } from "@/lib/format";
import { useLive } from "./LiveProvider";

const LIVE_FIELDS = ["ltp", "change", "change_pct", "oi", "volume", "best_bid", "best_ask", "status", "last_update"];
const mergeLive = (leg, live) => {
  if (!leg) return leg;
  const s = live?.get(leg.instrument_token);
  if (!s) return leg;
  const out = { ...leg };
  LIVE_FIELDS.forEach((f) => { if (s[f] !== undefined && s[f] !== null) out[f] = s[f]; });
  return out;
};

const Sel = ({ value, onChange, items, testid, w = "w-[150px]" }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className={`h-8 border-slate-800 bg-slate-900 font-mono text-xs ${w}`}><SelectValue /></SelectTrigger>
    <SelectContent className="max-h-72">
      {items.map((x) => <SelectItem key={x} value={String(x)} data-testid={`${testid}-${x}`}>{x}</SelectItem>)}
    </SelectContent>
  </Select>
);

const oiTone = (v) => (v > 0 ? "text-emerald-400" : v < 0 ? "text-rose-400" : "text-slate-500");

const Leg = ({ leg, side, greeks, onOpen }) => {
  if (!leg) {
    const span = greeks ? 6 : 4;
    return <td colSpan={span} className="px-2 text-center text-slate-700">—</td>;
  }
  const ltp = (
    <td key="l" onClick={() => onOpen({ ...leg, key: leg.tradingsymbol })} data-testid={`oc-${side}-ltp-${leg.tradingsymbol}`}
      className="cursor-pointer px-2 py-1.5 text-right font-semibold text-slate-100 hover:text-cyan-300">{fmt(leg.ltp)}</td>
  );
  const oiChg = (
    <td key="oic" className={`px-2 py-1.5 text-right ${oiTone(leg.oi_change)}`}>
      {leg.oi_change === null || leg.oi_change === undefined ? "—" : `${leg.oi_change > 0 ? "+" : ""}${fmtQty(leg.oi_change)}`}
    </td>
  );
  const oi = <td key="oi" className="px-2 py-1.5 text-right text-slate-400">{fmtQty(leg.oi)}</td>;
  const chg = <td key="c" className={`px-2 py-1.5 text-right ${tone(leg.change_pct)}`}>{fmtPct(leg.change_pct)}</td>;
  let cells;
  if (greeks) {
    const iv = (
      <td key="iv" className="px-2 py-1.5 text-right text-violet-300">
        <TooltipProvider><Tooltip>
          <TooltipTrigger asChild><span className="cursor-help underline decoration-dotted decoration-slate-600">{leg.iv ? `${leg.iv.toFixed(2)}%` : "—"}</span></TooltipTrigger>
          <TooltipContent className="border-slate-700 bg-slate-900 font-mono text-[11px] text-slate-200">
            <div>Δ delta {fmt(leg.delta, 3)}</div><div>Γ gamma {leg.gamma ?? "—"}</div>
            <div>Θ theta {fmt(leg.theta)}/day</div><div>ν vega {leg.vega ?? "—"}</div>
          </TooltipContent>
        </Tooltip></TooltipProvider>
      </td>
    );
    const delta = <td key="d" className="px-2 py-1.5 text-right text-slate-400">{fmt(leg.delta, 3)}</td>;
    cells = [oiChg, oi, iv, delta, chg, ltp];
  } else {
    cells = [oiChg, oi, chg, ltp];
  }
  return side === "ce" ? cells : [...cells].reverse();
};

export const OptionChain = ({ onOpen }) => {
  const [und, setUnd] = useState("NIFTY");
  const [exp, setExp] = useState("");
  const [n, setN] = useState("10");
  const [greeks, setGreeks] = useState(true);
  const { data: unds } = usePoll(() => get("/option-chain/underlyings"), 0, []);
  const { data: exps } = usePoll(() => get("/option-chain/expiries", { underlying: und }), 0, [und]);
  useEffect(() => { if (exps?.expiries?.length && !exps.expiries.includes(exp)) setExp(exps.expiries[0]); }, [exps, exp]);
  const { data, error } = usePoll(() => (exp && exps?.expiries?.includes(exp) ? get("/option-chain", { underlying: und, expiry: exp, strikes: n, greeks }) : Promise.resolve(null)), 2000, [und, exp, n, greeks]);
  const live = useLive();
  const tokens = useMemo(
    () => (data ? data.rows.flatMap((r) => [r.ce?.instrument_token, r.pe?.instrument_token].filter(Boolean)) : []),
    [data]
  );
  const tokenKey = tokens.join(",");
  useEffect(() => {
    if (!live || !tokens.length) return undefined;
    const ids = tokens.map(String);
    live.subscribe(ids);
    return () => live.unsubscribe(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenKey]);

  const CE_H = greeks ? ["OI Chg", "OI", "IV", "Delta", "Chg%", "LTP"] : ["OI Chg", "OI", "Chg%", "LTP"];
  const span = greeks ? 6 : 4;
  const liveOnly = unds && (unds.underlyings || []).length === 0;
  if (liveOnly) {
    return (
      <div data-testid="option-chain" className="p-3">
        <div data-testid="oc-live-only" className="rounded border border-rose-800/60 bg-rose-950/30 p-6 text-center text-sm text-rose-200">
          Live-only mode: no option chain until a Zerodha session is active.
        </div>
      </div>
    );
  }
  return (
    <div data-testid="option-chain" className="space-y-3 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Sel testid="oc-underlying-select" value={und} onChange={(v) => { setUnd(v); setExp(""); }} items={unds?.underlyings || [und]} />
        <Sel testid="oc-expiry-select" value={exp} onChange={setExp} items={exps?.expiries || []} />
        <Sel testid="oc-strikes-select" value={n} onChange={setN} items={["5", "10", "15", "20"]} w="w-[90px]" />
        <label className="flex items-center gap-2 rounded border border-slate-800 bg-slate-900 px-2.5 py-1.5 font-mono text-[11px] text-slate-300">
          <Switch data-testid="oc-greeks-switch" checked={greeks} onCheckedChange={setGreeks} /> Greeks
        </label>
        {data && (
          <div className="ml-auto flex flex-wrap gap-4 font-mono text-[11px] text-slate-500" data-testid="oc-summary">
            <span>SPOT <span className="text-slate-100">{fmt(data.spot)}</span></span>
            <span>ATM <span className="text-cyan-300">{data.atm_strike}</span></span>
            {greeks && <span>ATM IV <span className="text-violet-300" data-testid="oc-atm-iv">{data.atm_iv ? `${data.atm_iv.toFixed(2)}%` : "—"}</span></span>}
            <span>PCR <span className="text-slate-100" data-testid="oc-pcr">{data.pcr ?? "—"}</span></span>
            <span>LOT <span className="text-slate-100">{data.lot_size ?? "—"}</span></span>
          </div>
        )}
      </div>
      {data && (data.max_call_oi_build || data.max_put_oi_build) && (
        <div className="flex flex-wrap gap-4 font-mono text-[11px]" data-testid="oc-oi-build">
          {data.max_call_oi_build && <span className="text-rose-400">Highest call writing @ {data.max_call_oi_build}</span>}
          {data.max_put_oi_build && <span className="text-emerald-400">Highest put writing @ {data.max_put_oi_build}</span>}
        </div>
      )}
      {data?.cap_warning && <p className="text-xs text-amber-400" data-testid="oc-cap-warning">{data.cap_warning}</p>}
      {error && <p className="text-xs text-rose-400" data-testid="oc-error">{errMsg(error)}</p>}
      <div className="scroll-thin overflow-x-auto rounded border border-slate-800">
        <table className="w-full min-w-[760px] font-mono text-xs">
          <thead className="bg-slate-900/70 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th colSpan={span} className="py-1.5 text-center text-emerald-500/80">Calls</th>
              <th className="py-1.5 text-center">Strike</th>
              <th colSpan={span} className="py-1.5 text-center text-rose-500/80">Puts</th>
            </tr>
            <tr>
              {CE_H.map((h, i) => <th key={`c${h}${i}`} className="px-2 py-1 text-right">{h}</th>)}
              <th />
              {[...CE_H].reverse().map((h, i) => <th key={`p${h}${i}`} className="px-2 py-1 text-right">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {data?.rows.map((r) => (
              <tr key={r.strike} data-testid={`oc-row-${r.strike}`}
                className={`border-t border-slate-800/60 ${r.atm ? "bg-cyan-950/40" : r.strike < data.spot ? "bg-emerald-950/10" : ""}`}>
                <Leg leg={mergeLive(r.ce, live)} side="ce" greeks={greeks} onOpen={onOpen} />
                <td className={`px-3 py-1.5 text-center font-semibold ${r.atm ? "text-cyan-300" : "text-slate-300"}`}>{r.strike}</td>
                <Leg leg={mergeLive(r.pe, live)} side="pe" greeks={greeks} onOpen={onOpen} />
              </tr>
            ))}
          </tbody>
        </table>
        {!data && !error && <div className="m-3 h-40 animate-pulse rounded bg-slate-900" />}
      </div>
      <p className="text-[11px] text-slate-600">
        IV and Greeks are computed with Black-Scholes (OI change = vs today's first reading). Strikes around ATM are subscribed on demand and
        auto-released after inactivity. Click an LTP for depth & candles.
      </p>
    </div>
  );
};
